import { AfterViewInit, Component, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { AppConfiguration } from '../../configuration/configuration';
import { DvmService } from '../../services/dvm.service';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { ViewerLayoutComponent } from '../../layout/viewer-layout/viewer-layout.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService } from '@services/configuration.service';
import { MapViewerComponent } from "../../components/minisite/viewer/map-viewer/map-viewer.component";
import { Viewer3dComponent } from '@components/minisite/viewer/viewer3d/viewer3d.component';
import { PreviewService } from '@services/preview.service';
import { first, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { APP_PREVIEW, AppPreview } from '@configuration/preview';
import { ViewerMode } from '@models/enums/App/ViewerMode.enum';
import { DefaultConfig } from '@configuration/default-configuration';
import { PortalService } from '@services/portal.service';
import { LayoutService } from '@services/layout.service';
import { ToastService } from '@services/toast.service';
import { AmenitiesService } from '@services/amenities.service';
import { GroupAssignedAmenity } from '@models/interfaces/amenities/AmenityGroupAssigned.interface';
import { TemplateService } from '@services/template.service';
import { environment } from '../../../environments/environment';
import { CookiesComponent } from '@components/minisite/cookies/cookies.component';
import { SiteLoginComponent } from '@components/minisite/overlay/site-login/site-login.component';
import { Preview } from '@models/interfaces/core/Preview.interface';
import { TranslationService } from '@services/translation.service';
import { DIALOG_DATA } from '@angular/cdk/dialog';

@Component({
  selector: 'app-viewer',
  standalone: true,
  imports: [
    ViewerLayoutComponent,
    MapViewerComponent,
    Viewer3dComponent,
    CookiesComponent
  ],
  providers: [
    AppConfiguration,
    AppPreview,
    ToastService,
  ],
  templateUrl: './viewer.component.html',
  styleUrl: './viewer.component.css'
})
export class ViewerComponent implements OnInit, AfterViewInit, OnDestroy {

  // SERVICES
  private   readonly configuration:   ConfigurationService  = inject(ConfigurationService);
  private   readonly preview:         PreviewService        = inject(PreviewService);
  private   readonly router:          Router                = inject(Router);
  private   readonly route:           ActivatedRoute        = inject(ActivatedRoute);
  private   readonly layout:          LayoutService         = inject(LayoutService);
  private   readonly portal:          PortalService         = inject(PortalService);
  private   readonly toast:           ToastService          = inject(ToastService);
  protected readonly dvm:             DvmService            = inject(DvmService);
  private   readonly ams:             AmenitiesService      = inject(AmenitiesService);
  private   readonly template:        TemplateService       = inject(TemplateService);
  private   readonly translation:     TranslationService    = inject(TranslationService);

  
  // GLOBAL STATE
  private readonly unsubscribe$:      Subject<void>         = new Subject<void>();
  public  readonly ViewerMode = ViewerMode;
  
  // PREVIEW
  private readonly   preview$:        Signal<Preview> = inject(APP_PREVIEW);
  protected readonly importMode:      boolean         = inject(DIALOG_DATA, {optional: true})?.isImportMode || false; // Para cuando hacemos preview desde el modal de importación
  private            hasLogin:        boolean         = false;

  public get disabledByLogin(): boolean {

    return !this.hasLogin ? false :  // Si no tiene login, no deshabilitamos ( por defecto )
      this.preview$() ?              // Tenemos login, pero... tenemos preview?
        (localStorage.getItem(this.preview$()!.id.toString()) ? false : true) : // Tenemos login y preview, miramos cookie, si hay es false y si no es true
          true;                       // Tenemos login pero no preview, deshabilitamos siempre
        
  }
  public  readonly mode: ViewerMode = this.route.snapshot.data['mode'];

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
    this.translation.fetchGeneralTexts()
  }

  ngAfterViewInit(): void {
    this.initViewer()
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // LIFECYCLE METHODS
  /**
   * Inicializa el componente. 
   * Este método se llama antes de initViewer y es útil para inicializar funciones o servicios antes
   * de que se cargue el visor. 
   */
  private initComponent(): void {
    this.setLayoutBreakpoints();
  }

  /**
   * Inicializa el visor.
   * Recoge los parametros y carga el visor segun el modo que corresponda.
   */
  private initViewer(): void | Error {
    
    if(this.importMode) return this.loadImportedPreview();

    const team:      string | null     = this.route.snapshot.paramMap.get('team'),
          viewId:    string | null     = this.route.snapshot.queryParamMap.get('view_id') ? atob(this.route.snapshot.queryParamMap.get('view_id')!) : null,
          amenities: string | null     = this.route.snapshot.queryParamMap.get('amenities') ? atob(this.route.snapshot.queryParamMap.get('amenities')!) : null;

    switch (this.mode) {
      case ViewerMode.New:
      default:
        this.loadNew();
        break;
      case ViewerMode.View:
      case ViewerMode.Edit:
        this.loadPreview(team!, viewId!, amenities!);
        break;
    }
    
  }

  private loadNew(): void {
    this.configuration.loadConfig(structuredClone(DefaultConfig));
    this.dvm.initViewers();
  }

  private loadImportedPreview(): void {

    this.dvm.initViewers()

    return;

  }

  private loadPreview(team: string, viewId: string, amenities: string): void | Error {
    if (!team) throw new Error('No team provided in the URL. Cannot load viewer.');

    this.configuration.resetConfiguration();

    // Only check for redirect URL in View mode
    if (this.mode === ViewerMode.View) {
      this.preview.getRedirectUrl(team)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: ({ redirectUrl }) => {
            // If redirect URL exists, redirect and stop all processing
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }

            // No redirect - proceed with normal loading
            this.loadPreviewData(team, viewId, amenities);
          },
          error: error => {
            console.error('Error checking redirect URL', error);
            // If redirect verification fails, proceed with normal loading
            this.loadPreviewData(team, viewId, amenities);
          }
        });
    } else {
      // Edit mode - skip redirect check and load normally
      this.loadPreviewData(team, viewId, amenities);
    }
  }

  private loadPreviewData(team: string, viewId: string, amenities: string): void | Error {
    const preview$ = this.mode === ViewerMode.Edit
      ? this.preview.getPrivatePreview(team)
      : this.preview.getPublicPreview(team);
    
    // LOAD CONFIGURATION AND INIT VIEWERS
    preview$
      .pipe(
        tap(preview => preview.global_template ? this.template.setCurrentTemplate(preview.global_template) : null),
        switchMap(preview => {

          if(preview.accountMode && !localStorage.getItem(preview.id.toString())){

            const component: ComponentPortal<SiteLoginComponent> = new ComponentPortal<SiteLoginComponent>(SiteLoginComponent);
            this.portal.setPortal(component);
            this.hasLogin = true; 

          }

          this.preview.setPreview(preview);

          return this.preview.getMasterConfig(preview.path_master);
          
        })
      ).subscribe({
        next: config => {
          this.configuration.loadConfig(config, this.mode);
          this.dvm.initViewers();
        },
        error: error => {
          console.error('Error loading config', error);
            window.location.href = environment.notFoundUrl;
            return;
        }
      })

    // IF WE HAVE A VIEW ID, OPEN IT
    if (viewId) this.openParamView(viewId!);

    // IF WE HAVE AMENITIES, SELECT THEM
    if (amenities) this.setParamAmenities(amenities!);
  }

  private openParamView(viewId: string): void {

    const viewer3d: Viewer3dService = this.dvm.viewer3d;

    viewer3d.waitInitialize().subscribe((): void => {
      this.dvm.load3dView(viewId).subscribe(() => {
        if (!this.ams.hasSelectedAmenities()) {
          this.ams.checkSeatAmenity(viewId);
        }
      });
    })

  }

  private setParamAmenities(amenities: string | null): void {
    if (amenities) {
      const amenitiesIds = amenities.split(',');
      // using the parsed amenities IDs, get the full amenities from the service (amsMap)
      const amenitiesToSelect: GroupAssignedAmenity[] = [];

      this.ams.amsMapLoaded$.pipe(
        first(),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => {
        amenitiesIds.forEach(id => {
          const amenity: GroupAssignedAmenity | undefined = this.ams.amsMap.get(+id);
          if (amenity) amenitiesToSelect.push(amenity);
        });

        this.ams.updateSelectedAmenities(amenitiesToSelect);
      });
    }
  }

  private setLayoutBreakpoints(): void {

    // Inicializamos observable de seguimiento de layout.
    this.layout.trackLayout();

  }

}
